<template>
  <section class="workspace-stack">
    <article class="glass-surface workspace-panel workspace-panel--hero">
      <div class="workspace-panel__header workspace-panel__header--spread">
        <div>
          <span class="workspace-kicker">Códigos</span>
          <h2 class="workspace-title">Accesos, promos y promotores</h2>
          <p class="workspace-copy">Crea códigos individuales o carga varios de una sola vez sin mezclar flujos.</p>
        </div>

        <div class="workspace-actions workspace-actions--compact codes-hero-actions">
          <button class="workspace-button" type="button" @click="openCreateModal()">
            <i class="fas fa-plus"></i>
            <span>Crear código</span>
          </button>
          <button class="workspace-button workspace-button--primary" type="button" @click="openBulkModal()">
            <i class="fas fa-file-import"></i>
            <span>Carga masiva</span>
          </button>
        </div>
      </div>

      <div class="workspace-metrics">
        <article class="workspace-metric">
          <small>Total</small>
          <strong>{{ codes.length }}</strong>
        </article>
        <article class="workspace-metric">
          <small>Generales</small>
          <strong>{{ generalCodes.length }}</strong>
        </article>
        <article class="workspace-metric">
          <small>Promotores</small>
          <strong>{{ ambassadorCodes.length }}</strong>
        </article>
        <article class="workspace-metric">
          <small>Activos</small>
          <strong>{{ activeCodes.length }}</strong>
        </article>
      </div>
    </article>

    <p v-if="successMessage" class="workspace-feedback workspace-feedback--success">{{ successMessage }}</p>
    <p v-if="errorMessage" class="workspace-feedback workspace-feedback--error">{{ errorMessage }}</p>

    <article class="glass-surface workspace-panel codes-library-panel">
      <div class="workspace-panel__header workspace-panel__header--spread codes-library-panel__header">
        <div>
          <span class="workspace-kicker">Biblioteca</span>
          <h2 class="workspace-title codes-library-panel__title">Todos los códigos</h2>
          <p class="workspace-copy codes-library-panel__copy">Filtra, abre el link y administra estados sin salir del panel.</p>
        </div>
      </div>

      <div class="codes-toolbar">
        <label class="workspace-field codes-toolbar__search">
          <span>Buscar</span>
          <input v-model.trim="searchTerm" class="workspace-input" type="text" placeholder="Código, etiqueta, promotor o email" />
        </label>

        <div class="codes-filter-row" role="tablist" aria-label="Filtro de códigos">
          <button
            v-for="filter in filters"
            :key="filter.value"
            type="button"
            :class="['codes-filter-pill', { 'is-active': filterType === filter.value }]"
            @click="filterType = filter.value"
          >
            {{ filter.label }}
          </button>
        </div>
      </div>

      <div v-if="fetching && !codes.length" class="workspace-empty">Cargando códigos...</div>
      <div v-else-if="!filteredCodes.length" class="workspace-empty">No hay resultados para este filtro.</div>

        <div v-else class="codes-library">
          <div class="workspace-table-shell codes-library__table-shell">
            <table class="workspace-table codes-table-grid">
            <colgroup>
              <col style="width: 24%;" />
              <col style="width: 30%;" />
              <col style="width: 14%;" />
              <col style="width: 12%;" />
              <col style="width: 20%;" />
            </colgroup>
            <thead>
              <tr>
                <th>Código</th>
                <th>Detalle</th>
                <th>Link</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="eventCode in filteredCodes" :key="eventCode.id">
                <td class="codes-code-cell" data-label="Codigo">
                  <div class="workspace-table-title">{{ eventCode.code }}</div>
                  <div class="codes-table-pill-stack">
                    <span class="workspace-badge">{{ codeTypeLabel(eventCode.code_type) }}</span>
                    <span v-if="eventCode.code_type === 'ambassador'" class="workspace-badge">{{ inviteStatusLabel(eventCode.invite_status) }}</span>
                  </div>
                  <p class="workspace-table-subcopy">{{ eventCode.label || 'Sin etiqueta' }}</p>
                </td>
                <td class="codes-detail-cell" data-label="Detalle">
                  <template v-if="eventCode.code_type === 'ambassador'">
                    <div class="workspace-table-title">{{ eventCode.ambassador_display || eventCode.ambassador_name || 'Sin nombre' }}</div>
                    <p class="workspace-table-subcopy">{{ formatAmbassadorContact(eventCode) }}</p>
                    <p class="workspace-table-subcopy">{{ sourceLabel(eventCode.source) }} · {{ maxUsesLabel(eventCode.max_uses) }}</p>
                  </template>
                  <template v-else>
                    <div class="workspace-table-title">{{ eventCode.label || 'General' }}</div>
                    <p class="workspace-table-subcopy">{{ sourceLabel(eventCode.source) }} · {{ maxUsesLabel(eventCode.max_uses) }}</p>
                  </template>
                </td>
                <td class="codes-table-link-cell" data-label="Link">
                  <a class="workspace-inline-link" :href="fullPromoUrl(eventCode.code)" target="_blank" rel="noreferrer">Abrir link</a>
                </td>
                <td class="codes-status-cell" data-label="Estado">
                  <span :class="['workspace-status-pill', eventCode.is_active ? 'is-active' : 'is-off']">{{ eventCode.is_active ? 'Activo' : 'Archivado' }}</span>
                </td>
                <td class="codes-actions-cell" data-label="Acciones">
                  <div class="workspace-inline-actions codes-table-actions">
                    <button class="workspace-chip-button" type="button" @click="copyPromoLink(eventCode.code)">
                      <i class="fas fa-copy"></i>
                      <span>Copiar</span>
                    </button>
                    <button
                      v-if="eventCode.code_type === 'ambassador'"
                      class="workspace-chip-button"
                      type="button"
                      :disabled="invitingCodeId === eventCode.id || !eventCode.ambassador_email || eventCode.invite_status === 'claimed'"
                      @click="inviteAmbassador(eventCode)"
                    >
                      <i class="fas fa-paper-plane"></i>
                      <span>{{ inviteActionLabel(eventCode) }}</span>
                    </button>
                    <button class="workspace-chip-button" type="button" :disabled="togglingCodeId === eventCode.id" @click="toggleStatus(eventCode)">
                      <i :class="eventCode.is_active ? 'fas fa-eye-slash' : 'fas fa-rotate-left'"></i>
                      <span>{{ togglingCodeId === eventCode.id ? 'Guardando...' : eventCode.is_active ? 'Archivar' : 'Reactivar' }}</span>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </article>

    <WorkspaceWizardModal
      :model-value="createModalOpen"
      kicker="Códigos"
      title="Crear código"
      copy="Elige qué representa y completa los datos necesarios."
      size="lg"
      @close-request="closeCreateModal"
    >
      <form id="create-code-form" class="codes-modal-form" @submit.prevent="submitCreateCode">
        <section class="codes-section">
          <div class="codes-section__head">
            <h3>¿Qué representa este código?</h3>
            <p>Selecciona una opción para abrir solo los campos necesarios.</p>
          </div>

          <div class="codes-kind-grid">
            <button type="button" :class="['codes-kind-card', { 'is-active': createKind === 'general' }]" @click="createKind = 'general'">
              <span class="codes-kind-card__icon"><i class="fas fa-link"></i></span>
              <div>
                <strong>Código general</strong>
                <small>Para promociones o códigos de tracking.</small>
              </div>
            </button>

            <button type="button" :class="['codes-kind-card', { 'is-active': createKind === 'ambassador' }]" @click="createKind = 'ambassador'">
              <span class="codes-kind-card__icon"><i class="fas fa-user-check"></i></span>
              <div>
                <strong>Promotor</strong>
                <small>Para asignar ventas a un promotor y ver sus ventas.</small>
              </div>
            </button>
          </div>
        </section>

        <section v-if="createKind === 'ambassador'" class="codes-section">
          <div class="codes-section__head">
            <h3>Datos del promotor</h3>
            <p>Completa la ficha básica para identificarlo y poder invitarlo después.</p>
          </div>

          <div class="codes-field-grid">
            <label class="workspace-field">
              <span>Nombre</span>
              <input v-model.trim="createForm.ambassador_name" class="workspace-input" type="text" placeholder="Sofía Ramírez" required />
            </label>
            <label class="workspace-field">
              <span>Correo</span>
              <input v-model.trim="createForm.ambassador_email" class="workspace-input" type="email" placeholder="sofia@email.com" />
            </label>
            <label class="workspace-field">
              <span>Instagram</span>
              <input v-model.trim="createForm.ambassador_instagram" class="workspace-input" type="text" placeholder="@sofiaruns" />
            </label>
            <label class="workspace-field">
              <span>Teléfono</span>
              <input v-model.trim="createForm.ambassador_phone" class="workspace-input" type="text" placeholder="0999999999" />
            </label>
          </div>
        </section>

        <section class="codes-section">
          <div class="codes-section__head">
            <h3>Datos del código</h3>
          </div>

          <div class="codes-field-grid">
            <label class="workspace-field codes-field-grid__wide">
              <span>Código</span>
              <input v-model.trim="createForm.code" class="workspace-input" type="text" placeholder="VIP-LAUNCH" required />
            </label>
            <label class="workspace-field codes-field-grid__wide">
              <span>Etiqueta</span>
              <input v-model.trim="createForm.label" class="workspace-input" type="text" placeholder="Staff, campaña, influencer..." />
            </label>
            <label class="workspace-field codes-field-grid__narrow">
              <span>Máximo de usos</span>
              <input v-model="createForm.max_uses" class="workspace-input" type="number" min="1" placeholder="Opcional" />
            </label>
            <label class="codes-boolean-card" :class="{ 'is-active': createForm.is_active }">
              <input v-model="createForm.is_active" type="checkbox" />
              <strong>Crear activo</strong>
              <small>Queda listo para usar apenas guardes.</small>
            </label>
          </div>
        </section>

        <div class="codes-form-actions">
          <button class="workspace-button workspace-button--small workspace-button--ghost" type="button" @click="closeCreateModal">
            <i class="fas fa-arrow-left"></i>
            <span>Cancelar</span>
          </button>
          <button class="workspace-button workspace-button--small workspace-button--primary" type="submit" :disabled="createSaving">
            <i :class="createSaving ? 'fas fa-spinner fa-spin' : 'fas fa-save'"></i>
            <span>{{ createSaving ? 'Guardando...' : 'Crear código' }}</span>
          </button>
        </div>
      </form>
    </WorkspaceWizardModal>

    <WorkspaceWizardModal
      :model-value="bulkModalOpen"
      kicker="Códigos"
      title="Carga masiva"
      copy="Genera un lote o importa un CSV según el tipo de carga que necesites."
      size="lg"
      @close-request="closeBulkModal"
    >
      <form id="bulk-code-form" class="codes-modal-form" @submit.prevent="submitBulkCode">
        <section class="codes-section">
          <div class="codes-section__head">
            <h3>¿Cómo quieres cargar los códigos?</h3>
          </div>

          <div class="codes-bulk-mode-row" role="tablist" aria-label="Modo de carga">
            <button type="button" :class="['codes-bulk-mode-pill', { 'is-active': bulkMode === 'bulk' }]" @click="bulkMode = 'bulk'">Lote</button>
            <button type="button" :class="['codes-bulk-mode-pill', { 'is-active': bulkMode === 'csv' }]" @click="bulkMode = 'csv'">CSV</button>
          </div>
        </section>

        <section v-if="bulkMode === 'bulk'" class="codes-section">
          <div class="codes-section__head">
            <h3>Lote general</h3>
            <p>Crea varios códigos generales con la misma base. Si necesitas promotores, usa CSV.</p>
          </div>

          <div class="codes-field-grid">
            <label class="workspace-field">
              <span>Cantidad</span>
              <input v-model.number="bulkForm.quantity" class="workspace-input" type="number" min="1" max="500" />
            </label>
            <label class="workspace-field">
              <span>Prefijo</span>
              <input v-model.trim="bulkForm.prefix" class="workspace-input" type="text" placeholder="VIP" />
            </label>
            <label class="workspace-field codes-field-grid__wide">
              <span>Etiqueta base</span>
              <input v-model.trim="bulkForm.label_prefix" class="workspace-input" type="text" placeholder="Campaña influencers" />
            </label>
            <label class="workspace-field codes-field-grid__narrow">
              <span>Máximo de usos</span>
              <input v-model="bulkForm.max_uses" class="workspace-input" type="number" min="1" placeholder="Opcional" />
            </label>
          </div>
        </section>

        <template v-else>
          <section class="codes-section">
            <div class="codes-section__head codes-section__head--spread">
              <div>
                <h3>Archivo CSV</h3>
                <p>Elige una plantilla, descarga el demo y luego sube tu archivo.</p>
              </div>
              <button class="workspace-chip-button" type="button" @click="downloadCsvDemo">
                <i class="fas fa-file-download"></i>
                <span>CSV demo</span>
              </button>
            </div>

            <div class="codes-template-row" role="tablist" aria-label="Tipo de plantilla CSV">
              <button type="button" :class="['codes-template-pill', { 'is-active': csvTemplate === 'general' }]" @click="csvTemplate = 'general'">General</button>
              <button type="button" :class="['codes-template-pill', { 'is-active': csvTemplate === 'promoters' }]" @click="csvTemplate = 'promoters'">Promotores</button>
              <button type="button" :class="['codes-template-pill', { 'is-active': csvTemplate === 'mixed' }]" @click="csvTemplate = 'mixed'">Mixto</button>
            </div>

            <div
              :class="['codes-dropzone', { 'is-dragging': csvDragging, 'has-file': Boolean(importFile) }]"
              @dragenter.prevent="csvDragging = true"
              @dragover.prevent="csvDragging = true"
              @dragleave.prevent="csvDragging = false"
              @drop.prevent="onFileDrop"
            >
              <input ref="csvInput" class="codes-dropzone__input" type="file" accept=".csv" @change="onFileSelected" />
              <div class="codes-dropzone__copy">
                <strong>{{ importFile ? importFile.name : 'Arrastra tu CSV aquí' }}</strong>
                <p>{{ importFile ? `${csvPreview.rows.length} filas detectadas` : 'O selecciona un archivo desde tu computadora.' }}</p>
              </div>
              <div class="codes-dropzone__actions">
                <button class="workspace-button" type="button" @click.prevent="$refs.csvInput && $refs.csvInput.click()">
                  <i class="fas fa-upload"></i>
                  <span>{{ importFile ? 'Cambiar archivo' : 'Seleccionar archivo' }}</span>
                </button>
                <button v-if="importFile" class="workspace-button" type="button" @click.prevent="clearImportFile">
                  <i class="fas fa-times"></i>
                  <span>Quitar</span>
                </button>
              </div>
            </div>

            <div v-if="csvPreview.headers.length" class="codes-csv-preview">
              <div class="codes-csv-preview__meta">
                <span class="workspace-badge">Columnas: {{ csvPreview.headers.join(', ') }}</span>
                <span class="workspace-badge">Preview: {{ csvPreviewRows.length }} filas</span>
              </div>
              <div class="workspace-table-shell">
                <table class="workspace-table codes-csv-preview__table">
                  <thead>
                    <tr>
                      <th v-for="header in csvPreview.headers" :key="`header-${header}`">{{ header }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(row, rowIndex) in csvPreviewRows" :key="`row-${rowIndex}`">
                      <td v-for="header in csvPreview.headers" :key="`cell-${rowIndex}-${header}`">{{ row[header] || '-' }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          <section class="codes-section">
            <div class="codes-section__head">
              <h3>Defaults</h3>
              <p>Solo se usan cuando una fila no trae ese dato.</p>
            </div>

            <div class="codes-field-grid">
              <label class="workspace-field codes-field-grid__narrow">
                <span>Máximo de usos</span>
                <input v-model="csvDefaults.max_uses" class="workspace-input" type="number" min="1" placeholder="Opcional" />
              </label>
            </div>

            <div v-if="csvTemplate === 'promoters'" class="codes-ambassador-box">
              <label class="workspace-field">
                <span>Nombre</span>
                <input v-model.trim="csvDefaults.ambassador_name" class="workspace-input" type="text" placeholder="Sofía Ramírez" />
              </label>
              <label class="workspace-field">
                <span>Correo</span>
                <input v-model.trim="csvDefaults.ambassador_email" class="workspace-input" type="email" placeholder="sofia@email.com" />
              </label>
              <label class="workspace-field">
                <span>Instagram</span>
                <input v-model.trim="csvDefaults.ambassador_instagram" class="workspace-input" type="text" placeholder="@sofiaruns" />
              </label>
              <label class="workspace-field">
                <span>Teléfono</span>
                <input v-model.trim="csvDefaults.ambassador_phone" class="workspace-input" type="text" placeholder="0999999999" />
              </label>
            </div>
          </section>
        </template>

        <div class="codes-form-actions">
          <button class="workspace-button workspace-button--small workspace-button--ghost" type="button" @click="closeBulkModal">
            <i class="fas fa-arrow-left"></i>
            <span>Cancelar</span>
          </button>
          <button class="workspace-button workspace-button--small workspace-button--primary" type="submit" :disabled="bulkSaving">
            <i :class="bulkSaving ? 'fas fa-spinner fa-spin' : 'fas fa-save'"></i>
            <span>{{ bulkSaving ? 'Guardando...' : bulkMode === 'bulk' ? 'Generar lote' : 'Importar CSV' }}</span>
          </button>
        </div>
      </form>
    </WorkspaceWizardModal>
  </section>
</template>

<script>
import { apiService, apiServiceForm } from '../../common/api.services.js'
import WorkspaceWizardModal from '../../components/workspace/WorkspaceWizardModal.vue'

const DEFAULT_CREATE_FORM = () => ({
  code: '',
  label: '',
  is_active: true,
  max_uses: '',
  ambassador_name: '',
  ambassador_email: '',
  ambassador_instagram: '',
  ambassador_phone: '',
})

const DEFAULT_BULK_FORM = () => ({
  quantity: 10,
  prefix: '',
  label_prefix: '',
  max_uses: '',
})

const DEFAULT_CSV_DEFAULTS = () => ({
  max_uses: '',
  ambassador_name: '',
  ambassador_email: '',
  ambassador_instagram: '',
  ambassador_phone: '',
})

export default {
  name: 'CodesView',

  components: {
    WorkspaceWizardModal,
  },

  props: {
    event: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      fetching: false,
      createSaving: false,
      bulkSaving: false,
      togglingCodeId: null,
      invitingCodeId: null,
      codes: [],
      searchTerm: '',
      filterType: 'all',
      successMessage: '',
      errorMessage: '',
      createModalOpen: false,
      bulkModalOpen: false,
      createKind: 'general',
      bulkMode: 'bulk',
      csvTemplate: 'mixed',
      createForm: DEFAULT_CREATE_FORM(),
      bulkForm: DEFAULT_BULK_FORM(),
      csvDefaults: DEFAULT_CSV_DEFAULTS(),
      importFile: null,
      csvDragging: false,
      csvPreview: {
        headers: [],
        rows: [],
      },
    }
  },

  computed: {
    filters() {
      return [
        { value: 'all', label: 'Todos' },
        { value: 'general', label: 'Generales' },
        { value: 'ambassador', label: 'Promotores' },
        { value: 'archived', label: 'Archivados' },
      ]
    },

    activeCodes() {
      return this.codes.filter((code) => code.is_active)
    },

    generalCodes() {
      return this.codes.filter((code) => code.code_type !== 'ambassador')
    },

    ambassadorCodes() {
      return this.codes.filter((code) => code.code_type === 'ambassador')
    },

    filteredCodes() {
      const term = String(this.searchTerm || '').trim().toLowerCase()

      return [...this.codes]
        .filter((code) => {
          if (this.filterType === 'archived') return !code.is_active
          if (this.filterType === 'general') return code.code_type !== 'ambassador' && code.is_active
          if (this.filterType === 'ambassador') return code.code_type === 'ambassador' && code.is_active
          return true
        })
        .filter((code) => {
          if (!term) return true
          const haystack = [
            code.code,
            code.label,
            code.ambassador_name,
            code.ambassador_email,
            code.ambassador_instagram,
            code.ambassador_phone,
          ].join(' ').toLowerCase()
          return haystack.includes(term)
        })
        .sort((left, right) => {
          if (Boolean(left.is_active) !== Boolean(right.is_active)) {
            return left.is_active ? -1 : 1
          }

          return new Date(right.created_at || 0).getTime() - new Date(left.created_at || 0).getTime()
        })
    },

    csvPreviewRows() {
      return this.csvPreview.rows.slice(0, 5)
    },
  },

  created() {
    this.fetchCodes()
  },

  watch: {
    event: {
      deep: true,
      handler() {
        this.fetchCodes()
      },
    },
  },

  methods: {
    fetchCodes() {
      if (!this.event || !this.event.id) return

      this.fetching = true
      apiService(`/api/host/events/${this.event.id}/codes/`).then((data) => {
        this.codes = Array.isArray(data) ? data : []
      }).finally(() => {
        this.fetching = false
      })
    },

    resetMessages() {
      this.successMessage = ''
      this.errorMessage = ''
    },

    resetCreateForm() {
      this.createKind = 'general'
      this.createForm = DEFAULT_CREATE_FORM()
    },

    resetBulkForm() {
      this.bulkMode = 'bulk'
      this.csvTemplate = 'mixed'
      this.bulkForm = DEFAULT_BULK_FORM()
      this.csvDefaults = DEFAULT_CSV_DEFAULTS()
      this.importFile = null
      this.csvPreview = { headers: [], rows: [] }
      this.csvDragging = false
      if (this.$refs.csvInput) {
        this.$refs.csvInput.value = ''
      }
    },

    openCreateModal() {
      this.resetMessages()
      this.resetCreateForm()
      this.createModalOpen = true
    },

    closeCreateModal() {
      this.createModalOpen = false
      this.createSaving = false
      this.resetCreateForm()
    },

    openBulkModal() {
      this.resetMessages()
      this.resetBulkForm()
      this.bulkModalOpen = true
    },

    closeBulkModal() {
      this.bulkModalOpen = false
      this.bulkSaving = false
      this.resetBulkForm()
    },

    codeTypeLabel(value) {
      return value === 'ambassador' ? 'Promotor' : 'General'
    },

    sourceLabel(source) {
      if (Number(source) === 2) return 'Lote'
      if (Number(source) === 3) return 'Importado'
      return 'Manual'
    },

    inviteStatusLabel(value) {
      if (value === 'claimed') return 'Reclamado'
      if (value === 'invited') return 'Invitado'
      return 'Sin invitar'
    },

    maxUsesLabel(value) {
      return value ? `${value} usos máx.` : 'Sin límite'
    },

    formatInstagram(value) {
      const raw = String(value || '').trim()
      if (!raw) return ''
      return raw.startsWith('@') ? raw : `@${raw}`
    },

    formatAmbassadorContact(code) {
      return [
        code.ambassador_email || '',
        this.formatInstagram(code.ambassador_instagram),
        code.ambassador_phone || '',
      ].filter(Boolean).join(' · ') || 'Sin contacto'
    },

    fullPromoUrl(code) {
      return `${window.location.origin}/event/${this.event.slug}?co=${encodeURIComponent(code)}&wanted=true`
    },

    inviteActionLabel(eventCode) {
      if (!eventCode.ambassador_email) return 'Falta correo'
      if (this.invitingCodeId === eventCode.id) return 'Enviando...'
      if (eventCode.invite_status === 'claimed') return 'Reclamado'
      return eventCode.invite_status === 'invited' ? 'Reenviar' : 'Invitar'
    },

    copyPromoLink(code) {
      navigator.clipboard.writeText(this.fullPromoUrl(code)).then(() => {
        this.successMessage = `Link de ${code} copiado.`
      }).catch(() => {
        this.errorMessage = 'No se pudo copiar el link.'
      })
    },

    toggleStatus(eventCode) {
      this.resetMessages()
      this.togglingCodeId = eventCode.id

      const endpoint = `/api/host/events/codes/${eventCode.id}/`
      const method = eventCode.is_active ? 'DELETE' : 'PATCH'
      const payload = eventCode.is_active ? undefined : { is_active: true, code: eventCode.code, label: eventCode.label }

      apiService(endpoint, method, payload).then((data) => {
        if (data && data.err) {
          this.errorMessage = data.err || 'No se pudo actualizar el código.'
          return
        }

        this.successMessage = eventCode.is_active ? 'Código archivado.' : 'Código reactivado.'
        this.fetchCodes()
      }).finally(() => {
        this.togglingCodeId = null
      })
    },

    inviteAmbassador(eventCode) {
      if (!eventCode || !eventCode.id || !eventCode.ambassador_email || eventCode.invite_status === 'claimed') return

      this.resetMessages()
      this.invitingCodeId = eventCode.id

      apiService(`/api/host/events/codes/${eventCode.id}/invite/`, 'POST', {}).then((data) => {
        if (!data || data.err) {
          this.errorMessage = (data && data.err) || 'No se pudo enviar la invitación.'
          return
        }

        this.successMessage = `Invitación enviada a ${eventCode.ambassador_email}.`
        this.fetchCodes()
      }).finally(() => {
        this.invitingCodeId = null
      })
    },

    buildCreatePayload() {
      const payload = {
        code: String(this.createForm.code || '').trim(),
        label: String(this.createForm.label || '').trim(),
        is_active: Boolean(this.createForm.is_active),
        max_uses: this.createForm.max_uses ? Number(this.createForm.max_uses) : null,
        code_type: this.createKind === 'ambassador' ? 'ambassador' : 'promo',
      }

      if (this.createKind === 'ambassador') {
        payload.ambassador_name = String(this.createForm.ambassador_name || '').trim()
        payload.ambassador_email = String(this.createForm.ambassador_email || '').trim().toLowerCase()
        payload.ambassador_instagram = String(this.createForm.ambassador_instagram || '').trim()
        payload.ambassador_phone = String(this.createForm.ambassador_phone || '').trim()
      }

      return payload
    },

    submitCreateCode() {
      this.resetMessages()

      if (!String(this.createForm.code || '').trim()) {
        this.errorMessage = 'Escribe un código para continuar.'
        return
      }

      if (this.createKind === 'ambassador' && !String(this.createForm.ambassador_name || '').trim()) {
        this.errorMessage = 'Escribe el nombre del promotor.'
        return
      }

      this.createSaving = true
      apiService(`/api/host/events/${this.event.id}/codes/`, 'POST', this.buildCreatePayload())
        .then((data) => {
          if (!data || data.err) {
            this.errorMessage = (data && data.err) || 'No se pudo crear el código.'
            return
          }

          this.successMessage = `Código ${data.code} creado.`
          this.fetchCodes()
          this.closeCreateModal()
        })
        .finally(() => {
          this.createSaving = false
        })
    },

    onFileSelected(event) {
      const nextFile = event && event.target && event.target.files && event.target.files[0]
      if (!nextFile) return
      this.applyImportFile(nextFile)
    },

    onFileDrop(event) {
      this.csvDragging = false
      const nextFile = event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0]
      if (!nextFile) return
      this.applyImportFile(nextFile)
    },

    clearImportFile() {
      this.importFile = null
      this.csvPreview = { headers: [], rows: [] }
      if (this.$refs.csvInput) {
        this.$refs.csvInput.value = ''
      }
    },

    async applyImportFile(file) {
      this.resetMessages()
      this.importFile = file
      try {
        const text = await file.text()
        this.csvPreview = parseCsvText(text)
      } catch (error) {
        this.errorMessage = 'No pudimos leer el CSV.'
        this.importFile = null
        this.csvPreview = { headers: [], rows: [] }
      }
    },

    buildCsvDemoContent() {
      if (this.csvTemplate === 'general') {
        return [
          'code,label,max_uses,is_active',
          'PROMO-OPEN,Promo apertura,50,true',
          'TRACK-META,Tracking Meta,,true',
        ].join('\n')
      }

      if (this.csvTemplate === 'promoters') {
        return [
          'code,label,ambassador_name,ambassador_email,ambassador_instagram,ambassador_phone,max_uses,is_active',
          'SOFIA-PR,Promotor Sofia,Sofia Ramírez,sofia@email.com,@sofiaruns,0999999999,10,true',
          'MATEO-PR,Promotor Mateo,Mateo Díaz,mateo@email.com,@mateodiaz,0988888888,10,true',
        ].join('\n')
      }

      return [
        'code,label,code_type,ambassador_name,ambassador_email,ambassador_instagram,ambassador_phone,max_uses,is_active',
        'PROMO-OPEN,Promo apertura,promo,,,,,50,true',
        'TRACK-META,Tracking Meta,promo,,,,,,true',
        'SOFIA-PR,Promotor Sofia,ambassador,Sofia Ramírez,sofia@email.com,@sofiaruns,0999999999,10,true',
      ].join('\n')
    },

    downloadCsvDemo() {
      const blob = new Blob([this.buildCsvDemoContent()], { type: 'text/csv;charset=utf-8;' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `fomo-codes-${this.csvTemplate}.csv`
      link.click()
      window.URL.revokeObjectURL(url)
    },

    buildBulkPayload() {
      return {
        quantity: Number(this.bulkForm.quantity) || 0,
        prefix: String(this.bulkForm.prefix || '').trim(),
        label_prefix: String(this.bulkForm.label_prefix || '').trim(),
        max_uses: this.bulkForm.max_uses ? Number(this.bulkForm.max_uses) : null,
        code_type: 'promo',
      }
    },

    appendBulkCsvDefaults(formData) {
      if (this.csvDefaults.max_uses) {
        formData.append('max_uses', Number(this.csvDefaults.max_uses))
      }

      if (this.csvTemplate === 'general') {
        formData.append('code_type', 'promo')
      }

      if (this.csvTemplate === 'promoters') {
        formData.append('code_type', 'ambassador')
        if (this.csvDefaults.ambassador_name) formData.append('ambassador_name', this.csvDefaults.ambassador_name)
        if (this.csvDefaults.ambassador_email) formData.append('ambassador_email', this.csvDefaults.ambassador_email.toLowerCase())
        if (this.csvDefaults.ambassador_instagram) formData.append('ambassador_instagram', this.csvDefaults.ambassador_instagram)
        if (this.csvDefaults.ambassador_phone) formData.append('ambassador_phone', this.csvDefaults.ambassador_phone)
      }
    },

    submitBulkCode() {
      this.resetMessages()

      if (this.bulkMode === 'bulk') {
        const payload = this.buildBulkPayload()
        if (!payload.quantity) {
          this.errorMessage = 'Define la cantidad del lote.'
          return
        }

        this.bulkSaving = true
        apiService(`/api/host/events/${this.event.id}/codes/bulk/`, 'POST', payload)
          .then((data) => {
            if (!Array.isArray(data)) {
              this.errorMessage = (data && data.err) || 'No se pudo generar el lote.'
              return
            }

            this.successMessage = `Se generaron ${data.length} códigos.`
            this.fetchCodes()
            this.closeBulkModal()
          })
          .finally(() => {
            this.bulkSaving = false
          })

        return
      }

      if (!this.importFile) {
        this.errorMessage = 'Sube un archivo CSV antes de importar.'
        return
      }

      this.bulkSaving = true
      const formData = new FormData()
      formData.append('file', this.importFile)
      this.appendBulkCsvDefaults(formData)

      apiServiceForm(`/api/host/events/${this.event.id}/codes/import/`, 'POST', formData)
        .then((data) => {
          if (!data || data.err) {
            this.errorMessage = (data && data.err) || 'No se pudo importar el CSV.'
            return
          }

          this.successMessage = `Importados ${data.created || 0} códigos.`
          this.fetchCodes()
          this.closeBulkModal()
        })
        .finally(() => {
          this.bulkSaving = false
        })
    },
  },
}

function parseCsvText(text) {
  const lines = []
  let current = ''
  let row = []
  let inQuotes = false

  for (let index = 0; index < text.length; index += 1) {
    const char = text[index]
    const next = text[index + 1]

    if (char === '"') {
      if (inQuotes && next === '"') {
        current += '"'
        index += 1
      } else {
        inQuotes = !inQuotes
      }
      continue
    }

    if (char === ',' && !inQuotes) {
      row.push(current)
      current = ''
      continue
    }

    if ((char === '\n' || char === '\r') && !inQuotes) {
      if (char === '\r' && next === '\n') {
        index += 1
      }
      row.push(current)
      if (row.some((value) => String(value || '').trim() !== '')) {
        lines.push(row)
      }
      row = []
      current = ''
      continue
    }

    current += char
  }

  if (current || row.length) {
    row.push(current)
    if (row.some((value) => String(value || '').trim() !== '')) {
      lines.push(row)
    }
  }

  if (!lines.length) {
    return { headers: [], rows: [] }
  }

  const headers = lines[0].map((header) => String(header || '').trim())
  const parsedRows = []
  for (let rowIndex = 1; rowIndex < lines.length; rowIndex += 1) {
    const sourceRow = lines[rowIndex]
    const record = {}
    headers.forEach((header, headerIndex) => {
      record[header] = sourceRow[headerIndex] || ''
    })
    parsedRows.push(record)
  }

  return { headers, rows: parsedRows }
}
</script>

<style scoped>
.codes-hero-actions,
.codes-toolbar,
.codes-toolbar__search,
.codes-filter-row,
.codes-library,
.codes-table-pill-stack,
.codes-card,
.codes-card__head,
.codes-card__meta-row,
.codes-card__actions,
.codes-modal-form,
.codes-kind-grid,
.codes-bulk-mode-row,
.codes-template-row,
.codes-section,
.codes-section__head,
.codes-field-grid,
.codes-ambassador-box,
.codes-dropzone,
.codes-dropzone__copy,
.codes-dropzone__actions,
.codes-csv-preview,
.codes-csv-preview__meta,
.codes-modal-footer,
.codes-form-actions {
  display: grid;
  gap: 16px;
}

.codes-modal-form .workspace-button {
  border-radius: 12px;
  box-shadow: none;
}

.codes-library__mobile {
  display: none;
}

.codes-modal-form {
  padding-bottom: 40px;
}

.codes-hero-actions {
  grid-auto-flow: column;
}

.codes-library-panel__header {
  padding-bottom: 6px;
}

.codes-library-panel__title {
  font-size: clamp(1.9rem, 1.6rem + 0.7vw, 2.5rem);
  line-height: 1;
}

.codes-library-panel__copy {
  max-width: 640px;
}

.codes-toolbar {
  grid-template-columns: minmax(280px, 420px) minmax(0, 1fr);
  align-items: end;
  gap: 14px 20px;
}

.codes-filter-row,
.codes-bulk-mode-row,
.codes-template-row,
.codes-table-pill-stack {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.codes-filter-pill,
.codes-bulk-mode-pill,
.codes-template-pill {
  min-height: 38px;
  padding: 0 14px;
  border-radius: 999px;
  border: 1px solid rgba(196, 206, 228, 0.1);
  background: rgba(255, 255, 255, 0.03);
  color: var(--text-soft);
  font-weight: 700;
}

.codes-filter-pill.is-active,
.codes-bulk-mode-pill.is-active,
.codes-template-pill.is-active {
  color: var(--text-main);
  border-color: rgba(196, 206, 228, 0.24);
  background: rgba(255, 255, 255, 0.07);
}

.workspace-table-title {
  color: var(--text-main);
  font-weight: 700;
}

.workspace-table-subcopy {
  margin: 4px 0 0;
  color: var(--text-soft);
  line-height: 1.45;
}

.codes-table-grid {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

.codes-table-grid td {
  vertical-align: top;
  padding: 16px 12px 18px 0;
}

.codes-table-grid th {
  white-space: nowrap;
  padding: 0 0 14px;
}

.codes-table-grid th:last-child,
.codes-table-grid td:last-child {
  padding-right: 0;
}

.codes-actions-cell {
  min-width: 220px;
}

.codes-table-actions {
  gap: 6px;
  flex-wrap: wrap;
}

.codes-table-actions .workspace-chip-button {
  min-height: 34px;
  padding: 0 12px;
  border-radius: 10px;
  box-shadow: none;
  background: rgba(255, 255, 255, 0.035);
}

.codes-table-actions .workspace-chip-button span {
  font-size: 0.9rem;
}

.codes-table-link-cell .workspace-inline-link {
  white-space: nowrap;
}

.codes-section {
  padding: 18px;
  border-radius: 18px;
  border: 1px solid rgba(196, 206, 228, 0.08);
  background: rgba(255, 255, 255, 0.025);
}

.codes-section__head {
  display: grid;
  gap: 4px;
}

.codes-section__head h3 {
  margin: 0;
  color: var(--text-main);
}

.codes-section__head p {
  margin: 0;
  color: var(--text-soft);
  line-height: 1.55;
}

.codes-kind-grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.codes-kind-card {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 14px;
  align-items: start;
  padding: 18px;
  border-radius: 16px;
  border: 1px solid rgba(196, 206, 228, 0.1);
  background: rgba(255, 255, 255, 0.03);
  text-align: left;
  color: var(--text-main);
}

.codes-kind-card.is-active {
  border-color: rgba(196, 206, 228, 0.24);
  background: rgba(255, 255, 255, 0.07);
}

.codes-kind-card__icon {
  width: 36px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  border: 1px solid rgba(196, 206, 228, 0.12);
  background: rgba(255, 255, 255, 0.04);
}

.codes-kind-card strong {
  display: block;
  margin-bottom: 4px;
}

.codes-kind-card small {
  color: var(--text-soft);
  line-height: 1.5;
}

.codes-field-grid,
.codes-ambassador-box {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.codes-field-grid__wide {
  grid-column: 1 / -1;
}

.codes-field-grid__narrow {
  max-width: 240px;
}

.codes-boolean-card {
  position: relative;
  padding: 16px;
  border-radius: 16px;
  border: 1px solid rgba(196, 206, 228, 0.08);
  background: rgba(255, 255, 255, 0.025);
  display: grid;
  gap: 4px;
}

.codes-boolean-card.is-active {
  border-color: rgba(196, 206, 228, 0.24);
  background: rgba(255, 255, 255, 0.06);
}

.codes-boolean-card input {
  position: absolute;
  inset: 0;
  opacity: 0;
}

.codes-dropzone {
  padding: 18px;
  border-radius: 18px;
  border: 1px solid rgba(196, 206, 228, 0.1);
  background: rgba(255, 255, 255, 0.03);
}

.codes-dropzone.is-dragging,
.codes-dropzone.has-file {
  border-color: rgba(196, 206, 228, 0.24);
  background: rgba(255, 255, 255, 0.07);
}

.codes-dropzone__input {
  display: none;
}

.codes-dropzone__actions {
  grid-auto-flow: column;
  justify-content: start;
}

.codes-csv-preview__table {
  font-size: 0.88rem;
}

.codes-modal-footer {
  width: 100%;
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 12px;
}

.codes-modal-footer .workspace-button {
  min-width: 156px;
}

.codes-form-actions {
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
  margin-top: 6px;
  padding: 16px 0 24px;
  border-top: 1px solid rgba(196, 206, 228, 0.08);
}

.codes-form-actions .workspace-button {
  width: auto;
  min-width: 148px;
  flex: 0 0 auto;
  min-height: 42px;
  padding: 0 16px;
  border-radius: 12px;
  box-shadow: none;
}

@media (max-width: 1100px) {
  .codes-toolbar,
  .codes-kind-grid,
  .codes-field-grid,
  .codes-ambassador-box {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 900px) {
  .codes-hero-actions,
  .codes-dropzone__actions,
  .codes-modal-footer,
  .codes-form-actions {
    grid-auto-flow: row;
    display: grid;
  }

  .codes-hero-actions .workspace-button,
  .codes-modal-footer .workspace-button,
  .codes-form-actions .workspace-button {
    width: 100%;
  }

  .codes-table-grid thead {
    display: none;
  }

  .codes-table-grid,
  .codes-table-grid tbody,
  .codes-table-grid tr,
  .codes-table-grid td {
    display: block;
    width: 100%;
  }

  .codes-table-grid {
    min-width: 0;
    border: none;
    background: transparent;
  }

  .codes-table-grid tr {
    margin-bottom: 16px;
    padding: 16px;
    border-radius: 22px;
    border: 1px solid rgba(148, 163, 184, 0.18);
    background: linear-gradient(180deg, rgba(13, 20, 37, 0.98), rgba(8, 13, 24, 0.96));
    box-shadow: 0 18px 34px rgba(2, 6, 23, 0.28);
  }

  .codes-table-grid td {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    padding: 10px 0;
    border-bottom: 1px solid rgba(223, 214, 255, 0.08);
  }

  .codes-table-grid td:last-child {
    border-bottom: none;
  }

  .codes-table-grid td::before {
    content: attr(data-label);
    font-size: 0.72rem;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: rgba(208, 219, 238, 0.68);
    font-weight: 700;
    min-width: 96px;
    flex-shrink: 0;
  }

  .codes-code-cell,
  .codes-detail-cell,
  .codes-table-link-cell,
  .codes-status-cell,
  .codes-actions-cell {
    display: block;
  }

  .codes-code-cell::before,
  .codes-detail-cell::before,
  .codes-table-link-cell::before,
  .codes-status-cell::before,
  .codes-actions-cell::before {
    display: block;
    margin-bottom: 10px;
  }

  .codes-code-cell {
    padding-top: 0;
  }

  .codes-code-cell .workspace-table-title,
  .codes-detail-cell .workspace-table-title {
    font-size: 1.04rem;
    line-height: 1.15;
  }

  .codes-detail-cell .workspace-table-subcopy + .workspace-table-subcopy {
    margin-top: 6px;
  }

  .codes-status-cell .workspace-status-pill {
    display: inline-flex;
  }

  .codes-actions-cell {
    min-width: 0;
  }

  .codes-table-link-cell .workspace-inline-link {
    white-space: normal;
    overflow-wrap: anywhere;
  }

  .codes-table-actions {
    display: grid;
    grid-template-columns: 1fr;
  }

  .codes-table-actions .workspace-chip-button {
    width: 100%;
    justify-content: center;
  }
}
</style>
